import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { CheckCheck, Eye, Pencil, Pill as PillIcon, Trash2, UserCheck } from "lucide-react";
import moment from "moment";

import { AddTaskModal } from "components/AddTaskModal/AddTaskModal";
import { DisplayTaskModal } from "components/Modal/DisplayTaskModal/DisplayTaskModal";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Pill } from "components/ui/Pill";
import { PILL_COLORS } from "components/ui/Pill/pillColors";
import { useClaimTask } from "features/comunications/hooks/useClaimTask";
import { useCompleteTask } from "features/comunications/hooks/useCompleteTask";
import { useDeleteTask } from "features/comunications/hooks/useDeleteTask";
import { usePaginatedPrescriptionTasks } from "features/comunications/hooks/usePaginatedPrescriptionTasks";
import { canClaimTask, getTaskAssignmentLabel } from "features/comunications/utils/formatTaskAssignment";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useDebounce } from "hooks/useDebounce";
import { pathGenerators } from "utils/routes";

import s from "./Tasks.module.css";

const ClampedTitle = ({ text, className }) => {
  const ref = useRef(null);
  const [tooltip, setTooltip] = useState("");

  return (
    <Tooltip title={tooltip} placement="top" enterDelay={200}>
      <span
        ref={ref}
        className={className}
        onMouseEnter={() => {
          const el = ref.current;
          if (el && el.scrollHeight > el.clientHeight) setTooltip(text);
        }}
        onMouseLeave={() => setTooltip("")}
      >
        {text}
      </span>
    </Tooltip>
  );
};

const PRIORITY_LABELS = { 0: "High", 1: "Medium", 2: "Low" };
const PRIORITY_PILL = {
  0: PILL_COLORS.reactionYes,
  1: PILL_COLORS.statusWarning,
  2: PILL_COLORS.reactionNo,
};

export const PrescriptionTasks = ({ selectedPatient: _selectedPatient }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [taskToClaim, setTaskToClaim] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const debouncedSearch = useDebounce(searchTerm);

  const { data: paginatedData, isLoading } = usePaginatedPrescriptionTasks({
    page: currentPage,
    rows: itemsPerPage,
    searchTerm: debouncedSearch,
  });

  const { mutate: deleteTask, isPending: isDeleting } = useDeleteTask();
  const { mutate: completeTask, isPending: isCompleting } = useCompleteTask();
  const { mutate: claimTask, isPending: isClaiming } = useClaimTask();
  const { data: staffInfo } = useStaffInfo();

  const tasks = paginatedData?.data || [];
  const totalRecords = paginatedData?.totalRecords || 0;

  const columns = useMemo(
    () => [
      {
        id: "task",
        header: "",
        width: "minmax(0, 1fr)",
        render: (item, index) => (
          <div
            className={`${s.taskCell} ${
              item.patient?.id ? s.taskCellWithPatient : ""
            }`}
            onClick={() => setSelectedTask(item)}
          >
            <div className={s.taskDetails}>
              {/* Row 1: number + patient + date + actions */}
              <div className={s.taskTopRow}>
                <div className={s.taskTopLeft}>
                  <div className={s.taskNumber}>
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </div>
                  {item.taskRead === false && (
                    <div className={s.unreadIndicator} title="Unread task" />
                  )}
                  {item.patient?.id ? (
                    <Link
                      to={pathGenerators.patient.medication(item.patient.id)}
                      className={s.taskPatientLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.patient.firstName} {item.patient.surname}
                    </Link>
                  ) : (
                    <span className={s.taskPatientName}>—</span>
                  )}
                </div>
                <div className={s.taskTopRight}>
                  <div
                    className={s.taskActions}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.status !== "Complete" && (
                      <Tooltip
                        title="Mark as completed"
                        placement="top"
                        enterDelay={200}
                      >
                        <button
                          className={s.taskActionBtn}
                          onClick={() => setTaskToComplete(item)}
                        >
                          <CheckCheck size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {canClaimTask(item, staffInfo?.staffMember) && (
                      <Tooltip title="Claim task" placement="top" enterDelay={200}>
                        <button
                          className={s.taskActionBtn}
                          onClick={() => setTaskToClaim(item)}
                        >
                          <UserCheck size={16} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="View" placement="top" enterDelay={200}>
                      <button
                        className={s.taskActionBtn}
                        onClick={() => setSelectedTask(item)}
                      >
                        <Eye size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Edit" placement="top" enterDelay={200}>
                      <button
                        className={s.taskActionBtn}
                        onClick={() => setEditTask(item)}
                      >
                        <Pencil size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Delete" placement="top" enterDelay={200}>
                      <button
                        className={`${s.taskActionBtn} ${s.taskActionBtnDanger}`}
                        onClick={() => setTaskToDelete(item)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>

              {/* Row 2: pills with labels + date on right */}
              <div className={s.taskMeta}>
                <div className={s.taskMetaLeft}>
                  {PRIORITY_LABELS[item.priority] && (
                    <div className={s.taskMetaItem}>
                      <span className={s.taskMetaLabel}>Priority -</span>
                      <Pill {...PRIORITY_PILL[item.priority]}>
                        {PRIORITY_LABELS[item.priority]}
                      </Pill>
                    </div>
                  )}
                  <div className={s.taskMetaItem}>
                    <span className={s.taskMetaLabel}>Assigned to -</span>
                    <span>{getTaskAssignmentLabel(item)}</span>
                  </div>
                </div>
                {item.dueDate && (
                  <span className={s.taskDueDate}>
                    {moment(item.dueDate).format("DD-MM-YYYY")}
                  </span>
                )}
              </div>

              {/* Row 3: title/instructions (max 3 lines) */}
              <ClampedTitle text={item.title} className={s.taskTitle} />
            </div>
          </div>
        ),
      },
    ],
    [
      currentPage,
      itemsPerPage,
      setTaskToComplete,
      setTaskToClaim,
      setTaskToDelete,
      setSelectedTask,
      setEditTask,
      staffInfo,
    ]
  );

  return (
    <>
      <div className={s.container}>
        <div className={s.headerContainer}>
          <div className={s.headerTitleContainer}>
            <PillIcon size={20} style={{ color: "#6366f1" }} />
            <h2 className={s.headerTitle}>Prescriptions</h2>
            <div className={s.headerTasksQuantity}>{totalRecords}</div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={tasks}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          emptyTitle="No prescription tasks"
          emptyText="You don't have any prescription tasks at this time."
          hideHeader
          noScroll
          search={{
            value: searchTerm,
            onChange: (v) => {
              setSearchTerm(v);
              setCurrentPage(1);
            },
            placeholder: "Search prescriptions...",
          }}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalCount={totalRecords}
          rowsValue={itemsPerPage}
          onRowsChange={(val) => {
            setItemsPerPage(val);
            setCurrentPage(1);
          }}
          rowsOptions={[10, 20, 50, 100]}
        />
      </div>

      {selectedTask && (
        <DisplayTaskModal
          taskId={selectedTask.id}
          task={selectedTask}
          isOpen={!!selectedTask}
          closeModal={() => setSelectedTask(null)}
        />
      )}
      <AddTaskModal
        task={editTask}
        modalTitle="Edit Task"
        buttonText="Edit Task"
        noButton
        isOpen={!!editTask}
        setIsOpen={(open) => {
          if (!open) setEditTask(null);
        }}
      />

      <ConfirmDialog
        isOpen={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={() => {
          if (taskToDelete) {
            deleteTask(taskToDelete.id, {
              onSuccess: () => setTaskToDelete(null),
            });
          }
        }}
        isLoading={isDeleting}
        title="Delete task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        confirmLabel="Delete"
      />

      <ConfirmDialog
        isOpen={!!taskToComplete}
        onClose={() => setTaskToComplete(null)}
        onConfirm={() => {
          if (taskToComplete) {
            completeTask(taskToComplete.id, {
              onSuccess: () => setTaskToComplete(null),
            });
          }
        }}
        isLoading={isCompleting}
        title="Mark as completed"
        message="Are you sure you want to mark this task as completed?"
        confirmLabel="Mark completed"
      />

      <ConfirmDialog
        isOpen={!!taskToClaim}
        onClose={() => setTaskToClaim(null)}
        onConfirm={() => {
          if (taskToClaim) {
            claimTask(taskToClaim.id, {
              onSuccess: () => setTaskToClaim(null),
            });
          }
        }}
        isLoading={isClaiming}
        title="Claim task"
        message="This will assign the task only to you. Continue?"
        confirmLabel="Claim task"
      />
    </>
  );
};